import { cn } from '@lib/cn';
import { Badge, type BadgeTone } from '@shared/ui';

import {
    recordStatus,
    runFacts,
    runTotalCells,
    sheetCallouts,
    type Callout,
    type ClientSheet as Sheet,
} from '../model/report-sections';

import { ModulePreview } from './ModulePreview';
import { RecordTable } from './RecordTable';

import type { ModuleReportView } from '../model/module-report-types';
import type { ColumnDef } from '../schemas/bulk-migration';

/**
 * One launched client × record, as a sheet: a head (who, and the chips that
 * say how it went), a body in two columns — the record's mapping on the left,
 * the module's preview and the run on the right — and every warning or
 * failure as a callout underneath. Never split across printed pages.
 */

const ACCENT: Partial<Record<BadgeTone, string>> = {
    danger: 'border-l-danger-solid',
    warning: 'border-l-warning-solid',
    info: 'border-l-info-solid',
};

function CalloutBox({ callout }: { callout: Callout }) {
    return (
        <div
            role="note"
            className={cn(
                'grid gap-1 rounded-r-lg border border-l-[3px] border-line px-3.5 py-3 text-[13.5px]',
                ACCENT[callout.tone] ?? 'border-l-content-faint',
            )}
        >
            <span className="font-mono text-[12px] font-medium text-content-subtle">
                {callout.code} · {callout.tag}
            </span>
            <p className="text-content-body">{callout.message}</p>
        </div>
    );
}

function RunBlock({ sheet, view }: { sheet: Sheet; view: ModuleReportView }) {
    const facts = [...runFacts(sheet), ...(sheet.record ? view.facts(sheet.record) : [])];
    return (
        <div className="grid gap-2.5">
            <h4 className="text-[12px] font-semibold uppercase tracking-wider text-content-muted">
                Run
            </h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 print:grid-cols-4">
                {runTotalCells(sheet.row).map((cell) => (
                    <div
                        key={cell.label}
                        className="grid rounded-lg border border-line-subtle px-2.5 py-2"
                    >
                        <b
                            className={cn(
                                'text-[20px] font-semibold tabular-nums leading-tight',
                                cell.value === null || cell.value === 0
                                    ? 'text-content-faint'
                                    : 'text-content',
                            )}
                        >
                            {cell.value ?? '—'}
                        </b>
                        <span className="text-[11.5px] text-content-muted">{cell.label}</span>
                    </div>
                ))}
            </div>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 text-[13px]">
                {facts.map((fact) => (
                    <div key={fact.label} className="contents">
                        <dt className="text-content-muted">{fact.label}</dt>
                        <dd
                            className={cn(
                                'min-w-0 tabular-nums [overflow-wrap:anywhere]',
                                fact.mono && 'font-mono text-[11.5px]',
                            )}
                        >
                            {fact.value}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

export function ClientSheet({
    sheet,
    view,
    columns,
}: {
    sheet: Sheet;
    view: ModuleReportView;
    columns: readonly ColumnDef[];
}) {
    const { row, record } = sheet;
    const status = recordStatus(sheet);
    const chip = record === null ? null : view.recordChip(record);
    const preview = record === null ? null : view.preview(record, row.tenant.name);
    const callouts = sheetCallouts(sheet);

    return (
        <article className="break-inside-avoid overflow-hidden rounded-xl border border-line bg-surface shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-line px-[22px] py-5">
                <div className="grid min-w-0 gap-1">
                    <h3 className="text-[20px] font-semibold tracking-tight text-content">
                        {row.tenant.name}
                    </h3>
                    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[13px] text-content-muted">
                        {row.tenant.slug && <span className="font-mono">{row.tenant.slug}</span>}
                        {row.tenant.orgId && (
                            <span>
                                Org <span className="font-mono">{row.tenant.orgId}</span>
                            </span>
                        )}
                        <span>{row.tenant.isSandbox ? 'Sandbox' : 'Production'}</span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    <Badge tone={row.tenant.status === 'active' ? 'success' : 'warning'} dot>
                        Tenant {row.tenant.status}
                    </Badge>
                    <Badge tone={status.tone} dot>
                        {status.label}
                    </Badge>
                    {record?.tracking && (
                        <Badge tone="neutral">Attempts: {record.tracking.attempts}</Badge>
                    )}
                </div>
            </div>

            <div className="grid min-[820px]:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] print:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
                <div className="min-w-0 px-[22px] py-5">
                    {record === null ? (
                        <p className="text-[13.5px] text-content-muted">
                            No record was read for this client.
                        </p>
                    ) : (
                        <>
                            <div className="mb-3.5 flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
                                <h4 className="text-[17px] font-semibold text-content">
                                    {record.title}
                                </h4>
                                <span className="font-mono text-[12px] text-content-muted">
                                    {record.sourceId}
                                </span>
                                {chip && <Badge tone="neutral">{chip}</Badge>}
                            </div>
                            <RecordTable table={view.table(record, columns)} />
                        </>
                    )}
                </div>
                <div className="grid min-w-0 content-start gap-[22px] border-t border-line px-[22px] py-5 min-[820px]:border-l min-[820px]:border-t-0 print:border-l print:border-t-0">
                    {preview && <ModulePreview preview={preview} />}
                    <RunBlock sheet={sheet} view={view} />
                </div>
            </div>

            {callouts.length > 0 && (
                <div className="grid gap-2.5 px-[22px] pb-5">
                    {callouts.map((callout, index) => (
                        <CalloutBox key={`${callout.code}-${String(index)}`} callout={callout} />
                    ))}
                </div>
            )}
        </article>
    );
}
